import { useEffect } from 'react'
import { isRouteErrorResponse, useHref, useRouteError } from 'react-router'
import { isStaleBuildError, mayReloadForNewBuild, reloadForNewBuild } from '@/lib/staleBuild'

/**
 * Catches anything a route throws — a render error, a failed lazy import,
 * an unknown URL — so the user gets a message instead of a blank page. A lazy
 * import that failed because a release replaced its file reloads the page once
 * for the new release (lib/staleBuild) instead.
 */
export function RouteError() {
  const error = useRouteError()
  // The start of whichever app this is: the workspace's lives under /app.
  const start = useHref('/')
  // Decided while rendering (a read only); the effect does the reload.
  const reloading = isStaleBuildError(error) && mayReloadForNewBuild()

  useEffect(() => {
    if (reloading) reloadForNewBuild()
  }, [reloading])

  if (reloading) {
    return (
      <main className="mx-auto max-w-xl px-6 py-24 text-center">
        <p className="text-sm">Loading the new version… · جارٍ تحميل الإصدار الجديد…</p>
      </main>
    )
  }

  const title = isRouteErrorResponse(error)
    ? error.status === 404
      ? 'Page not found'
      : `${error.status} ${error.statusText}`
    : 'Something went wrong'

  const detail = isRouteErrorResponse(error)
    ? undefined
    : error instanceof Error
      ? error.message
      : undefined

  return (
    <main className="mx-auto max-w-xl px-6 py-24 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      {detail !== undefined && import.meta.env.DEV && (
        <pre className="mt-6 overflow-x-auto rounded-lg bg-slate-100 p-4 text-left text-sm dark:bg-slate-900">
          {detail}
        </pre>
      )}
      <a href={start} className="mt-8 inline-block text-sm font-medium underline underline-offset-4">
        Back to start
      </a>
    </main>
  )
}

/**
 * What the first screen shows while its lazily loaded chunk arrives (React Router's HydrateFallback): nothing, as
 * before — declared on every route tree's root so the router stops warning "No `HydrateFallback` element provided".
 */
export function Hydrating() {
  return null
}
